import React, { useEffect, useMemo, useState } from "react";

import { getAuth } from "firebase/auth";

import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import {
  Eye,
  Shield,
  Loader2,
} from "lucide-react";

import { db } from "../web/src/firebaseClient";

import InnovationHubHeader from "../components/InnovationHubHeader";
import InnovationHubFooter from "../components/InnovationHubFooter";

export default function AdminModarationManagement() {
  const [access, setAccess] = useState(null);

  const [mainTab, setMainTab] =
    useState("starter");

  const [contentTab, setContentTab] =
    useState("applications");

  const [statusFilter, setStatusFilter] =
    useState("pending");

  const [starterApplications, setStarterApplications] =
    useState([]);

  const [starterProjects, setStarterProjects] =
    useState([]);

  const [researchApplications, setResearchApplications] =
    useState([]);

  const [researchProjects, setResearchProjects] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  useEffect(() => {
    const checkAccessAndFetch = async () => {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        setAccess({
          isAdmin: false,
          isSupervisor: false,
          canAccess: false,
        });

        setLoading(false);
        return;
      }

      try {
        // ADMIN KONTROLÜ
        const token = await user.getIdTokenResult();

        const isAdmin =
          token.claims.role === "admin";

        // SUPERVISOR KONTROLÜ
        let isSupervisor = false;

        const usersSnapshot = await getDocs(
          query(
            collection(db, "users"),
            where("email", "==", user.email)
          )
        );

        if (!usersSnapshot.empty) {
          const userData =
            usersSnapshot.docs[0].data();

          isSupervisor =
            userData.roles?.supervisor === true;
        }

        const canAccess =
          isAdmin || isSupervisor;

        setAccess({
          isAdmin,
          isSupervisor,
          canAccess,
        });

        if (!canAccess) {
          setLoading(false);
          return;
        }

        await fetchAllData();
      } catch (error) {
        console.error(
          "Yetki kontrolü sırasında hata:",
          error
        );

        setAccess({
          isAdmin: false,
          isSupervisor: false,
          canAccess: false,
        });

        setLoading(false);
      }
    };

    checkAccessAndFetch();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);

    try {
      await Promise.all([
        fetchStarterApplications(),
        fetchStarterProjects(),
        fetchResearchApplications(),
        fetchResearchProjects(),
      ]);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStarterApplications =
    async () => {
      const snapshot = await getDocs(
        collection(db, "starterApplications")
      );

      const list = snapshot.docs.map(
        (docSnap) => ({
          id: docSnap.id,
          type: "starterApplication",
          ...docSnap.data(),
        })
      );

      setStarterApplications(list);
    };

  const fetchStarterProjects =
    async () => {
      const snapshot = await getDocs(
        collection(db, "starterProjects")
      );

      const list = snapshot.docs.map(
        (docSnap) => ({
          id: docSnap.id,
          type: "starterProject",
          ...docSnap.data(),
        })
      );

      setStarterProjects(list);
    };

  const fetchResearchApplications =
    async () => {
      const snapshot = await getDocs(
        collection(
          db,
          "researchProjectApplications"
        )
      );

      const list = snapshot.docs.map(
        (docSnap) => ({
          id: docSnap.id,
          type: "researchApplication",
          ...docSnap.data(),
        })
      );

      setResearchApplications(list);
    };

  const fetchResearchProjects =
    async () => {
      const snapshot = await getDocs(
        collection(db, "researchProjects")
      );

      const list = snapshot.docs.map(
        (docSnap) => ({
          id: docSnap.id,
          type: "researchProject",
          ...docSnap.data(),
        })
      );

      setResearchProjects(list);
    };

  const currentItems = useMemo(() => {
    let items = [];

    if (
      mainTab === "starter" &&
      contentTab === "applications"
    ) {
      items = starterApplications;
    }

    if (
      mainTab === "starter" &&
      contentTab === "projects"
    ) {
      items = starterProjects;
    }

    if (
      mainTab === "research" &&
      contentTab === "applications"
    ) {
      items = researchApplications;
    }

    if (
      mainTab === "research" &&
      contentTab === "projects"
    ) {
      items = researchProjects;
    }

    return items.filter(
      (item) =>
        (item.status || "pending") ===
        statusFilter
    );
  }, [
    mainTab,
    contentTab,
    statusFilter,
    starterApplications,
    starterProjects,
    researchApplications,
    researchProjects,
  ]);

  if (access === null || loading) {
    return (
      <main className="min-h-screen bg-[#eef5ff]">
        <InnovationHubHeader />

        <div className="flex items-center gap-2 p-6 text-[#173b7a]">
          <Loader2
            className="animate-spin"
            size={18}
          />
          Yükleniyor...
        </div>
      </main>
    );
  }

  if (!access.canAccess) {
    return (
      <main className="min-h-screen bg-[#eef5ff]">
        <InnovationHubHeader />

        <div className="p-10 text-center">
          <h2 className="text-xl font-semibold text-[#C62828]">
            Bu sayfaya erişim yetkiniz yok.
          </h2>
        </div>
      </main>
    );
  }

  return (
    <main
      className="min-h-screen bg-[#eef5ff]"
      style={{
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <InnovationHubHeader />

      <section className="px-4 py-6 md:px-6">
        <div className="mx-auto max-w-[1400px]">
          <div className="mb-6 rounded-2xl bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#1565C0]/10 text-[#1565C0]">
                <Shield size={22} />
              </div>

              <div>
                <h1 className="text-2xl font-semibold text-[#173b7a]">
                  Moderasyon
                </h1>

                <p className="text-sm text-slate-500">
                  Başvuruları ve projeleri yönetin
                </p>
              </div>
            </div>
          </div>

          <div className="mb-6 rounded-[28px] bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center gap-4">
              <ToggleGroup>
                <ToggleItem
                  active={mainTab === "starter"}
                  onClick={() =>
                    setMainTab("starter")
                  }
                >
                  İTK Starter
                </ToggleItem>

                <ToggleItem
                  active={mainTab === "research"}
                  onClick={() =>
                    setMainTab("research")
                  }
                >
                  Araştırma Projeleri
                </ToggleItem>
              </ToggleGroup>

              <ToggleGroup>
                <ToggleItem
                  active={
                    contentTab === "applications"
                  }
                  onClick={() =>
                    setContentTab("applications")
                  }
                >
                  Başvurular
                </ToggleItem>

                <ToggleItem
                  active={contentTab === "projects"}
                  onClick={() =>
                    setContentTab("projects")
                  }
                >
                  Projeler
                </ToggleItem>
              </ToggleGroup>

              <ToggleGroup>
                <ToggleItem
                  active={
                    statusFilter === "pending"
                  }
                  onClick={() =>
                    setStatusFilter("pending")
                  }
                >
                  Onay Bekleyenler
                </ToggleItem>

                <ToggleItem
                  active={
                    statusFilter === "approved"
                  }
                  onClick={() =>
                    setStatusFilter("approved")
                  }
                >
                  Onaylananlar
                </ToggleItem>

                <ToggleItem
                  active={
                    statusFilter === "rejected"
                  }
                  onClick={() =>
                    setStatusFilter("rejected")
                  }
                >
                  Reddedilenler
                </ToggleItem>
              </ToggleGroup>
            </div>
          </div>

          {currentItems.length === 0 ? (
            <div className="rounded-2xl bg-white p-6 text-sm text-slate-500 shadow-sm">
              Veri bulunmuyor.
            </div>
          ) : (
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {currentItems.map((item) =>
                item.type ===
                  "researchApplication" ||
                item.type ===
                  "researchProject" ? (
                  <ResearchCard
                    key={item.id}
                    item={item}
                    contentTab={contentTab}
                  />
                ) : (
                  <StarterCard
                    key={item.id}
                    item={item}
                    contentTab={contentTab}
                  />
                )
              )}
            </div>
          )}
        </div>
      </section>

      <InnovationHubFooter />
    </main>
  );
}

function ToggleGroup({ children }) {
  return (
    <div className="inline-flex rounded-[24px] bg-[#edf4ff] p-1.5 shadow-inner">
      <div className="flex flex-wrap gap-1">
        {children}
      </div>
    </div>
  );
}

function ToggleItem({
  active,
  onClick,
  children,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-[18px] px-5 py-2.5 text-sm font-semibold transition-all duration-200 ${
        active
          ? "bg-white text-[#1565C0] shadow-md"
          : "text-slate-500 hover:bg-white/70 hover:text-[#1565C0]"
      }`}
    >
      {children}
    </button>
  );
}

function StarterCard({
  item,
  contentTab,
}) {
  const detailPath =
    contentTab === "applications"
      ? `/starter-applications/${item.id}`
      : `/starter-projects/${item.id}`;

  return (
    <article className="rounded-2xl bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
      <div className="mb-4">
        <span className="rounded-full bg-[#1565C0]/10 px-3 py-1 text-xs font-semibold text-[#1565C0]">
          {contentTab === "applications"
            ? "İTK Starter Başvurusu"
            : "İTK Starter Projesi"}
        </span>
      </div>

      <div className="space-y-4">
        <div className="rounded-xl bg-[#f1f6ff] p-3">
          <p className="text-xs font-medium text-slate-400">
            Takım Lideri
          </p>

          <p className="mt-1 text-sm font-semibold text-slate-700">
            {item.teamLeader ||
              "Belirtilmemiş"}
          </p>
        </div>

        <div>
          <p className="text-xs font-medium text-slate-400">
            Problem
          </p>

          <p className="mt-1 line-clamp-4 text-sm leading-relaxed text-slate-600">
            {item.problem || "Belirtilmemiş"}
          </p>
        </div>

        <div>
          <p className="text-xs font-medium text-slate-400">
            Çözüm
          </p>

          <p className="mt-1 line-clamp-4 text-sm leading-relaxed text-slate-600">
            {item.solution || "Belirtilmemiş"}
          </p>
        </div>
      </div>

      <div className="mt-5 flex justify-end">
        <button
          type="button"
          onClick={() => {
            window.location.href = detailPath;
          }}
          className="flex items-center gap-2 rounded-xl bg-[#1565C0] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#0f57aa]"
        >
          <Eye size={16} />
          İncele
        </button>
      </div>
    </article>
  );
}

function ResearchCard({
  item,
  contentTab,
}) {
  const detailPath =
    contentTab === "applications"
      ? `/research-projects/${item.id}`
      : `/research-papers/${item.id}`;

  return (
    <article className="rounded-2xl bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
      <div className="mb-4">
        <span className="rounded-full bg-[#1565C0]/10 px-3 py-1 text-xs font-semibold text-[#1565C0]">
          {contentTab === "applications"
            ? "Araştırma Başvurusu"
            : "Araştırma Projesi"}
        </span>
      </div>

      <div className="space-y-4">
        <div className="rounded-xl bg-[#f1f6ff] p-3">
          <p className="text-xs font-medium text-slate-400">
            Başvuran
          </p>

          <p className="mt-1 text-sm font-semibold text-slate-700">
            {item.applicantName ||
              "Belirtilmemiş"}
          </p>
        </div>

        <div>
          <p className="text-xs font-medium text-slate-400">
            Proje Özeti
          </p>

          <p className="mt-1 line-clamp-5 text-sm leading-relaxed text-slate-600">
            {item.projectSummary ||
              "Belirtilmemiş"}
          </p>
        </div>
      </div>

      <div className="mt-5 flex justify-end">
        <button
          type="button"
          onClick={() => {
            window.location.href = detailPath;
          }}
          className="flex items-center gap-2 rounded-xl bg-[#1565C0] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#0f57aa]"
        >
          <Eye size={16} />
          İncele
        </button>
      </div>
    </article>
  );
}